import React from 'react';
import { Link } from 'react-router-dom';
import '../assets/styles/landing.css';

const LandingPage = () => {
  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-container">
          <Link to="/" className="landing-brand">
            <i className="fas fa-shield-alt"></i>
            <span>SafeWork<span className="brand-360">360</span></span>
          </Link>
          <div className="landing-nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#pricing" className="nav-link">Pricing</a>
            <a href="#about" className="nav-link">About</a>
            <Link to="/login" className="nav-link">Sign In</Link>
            <Link to="/register" className="btn-get-started">Get Started Free</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>
              Environmental & OHS
              <span className="highlight"> Compliance</span>
              <br />Made Simple
            </h1>
            <p className="hero-subtitle">
              Monitor your projects, track inspections, manage incidents, and ensure 
              regulatory compliance with one powerful platform built for your business.
            </p>
            <div className="hero-actions">
              <Link to="/register" className="btn-primary-hero">
                <i className="fas fa-rocket me-2"></i>
                Start Free Trial
              </Link>
              <Link to="/login" className="btn-secondary-hero">
                <i className="fas fa-sign-in-alt me-2"></i>
                Sign In
              </Link>
            </div>
            <div className="hero-trust">
              <span><i className="fas fa-check-circle"></i> No credit card required</span>
              <span><i className="fas fa-check-circle"></i> 14-day free trial</span>
              <span><i className="fas fa-check-circle"></i> Cancel anytime</span>
            </div>
          </div>
          <div className="hero-visual">
            <div className="dashboard-preview">
              <div className="preview-header">
                <div className="preview-dots">
                  <span></span><span></span><span></span>
                </div>
                <span className="preview-title">Dashboard Overview</span>
              </div>
              <div className="preview-content">
                <div className="preview-stat green">
                  <i className="fas fa-clipboard-check"></i>
                  <div>
                    <span className="stat-number">94%</span>
                    <span className="stat-label">Compliance Rate</span>
                  </div>
                </div>
                <div className="preview-stat blue">
                  <i className="fas fa-project-diagram"></i>
                  <div>
                    <span className="stat-number">12</span>
                    <span className="stat-label">Active Projects</span>
                  </div>
                </div>
                <div className="preview-stat orange">
                  <i className="fas fa-tasks"></i>
                  <div>
                    <span className="stat-number">28</span>
                    <span className="stat-label">Pending Tasks</span>
                  </div>
                </div>
                <div className="preview-stat purple">
                  <i className="fas fa-users"></i>
                  <div>
                    <span className="stat-number">45</span>
                    <span className="stat-label">Team Members</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-features">
        <div className="features-container">
          <div className="section-header">
            <h2>Everything you need to stay compliant</h2>
            <p>Powerful features to manage environmental and occupational health & safety requirements</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon green">
                <i className="fas fa-project-diagram"></i>
              </div>
              <h3>Project Management</h3>
              <p>Track multiple projects with EIA certificates, components, and conditions all in one place.</p>
              <Link to="/register" className="feature-link">Learn more <i className="fas fa-arrow-right"></i></Link>
            </div>

            <div className="feature-card">
              <div className="feature-icon blue">
                <i className="fas fa-clipboard-check"></i>
              </div>
              <h3>Inspections & Monitoring</h3>
              <p>Schedule inspections, assign tasks to field officers, and collect evidence with geo-tagging.</p>
              <Link to="/register" className="feature-link">Learn more <i className="fas fa-arrow-right"></i></Link>
            </div>

            <div className="feature-card">
              <div className="feature-icon orange">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3>Incident Management</h3>
              <p>Report and track incidents, conduct investigations, and implement corrective actions.</p>
              <Link to="/register" className="feature-link">Learn more <i className="fas fa-arrow-right"></i></Link>
            </div>

            <div className="feature-card">
              <div className="feature-icon purple">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3>Reports & Analytics</h3>
              <p>Generate compliance reports, track trends, and gain insights with powerful analytics.</p>
              <Link to="/register" className="feature-link">Learn more <i className="fas fa-arrow-right"></i></Link>
            </div>

            <div className="feature-card">
              <div className="feature-icon teal">
                <i className="fas fa-users"></i>
              </div>
              <h3>Team Collaboration</h3>
              <p>Manage your team with role-based access control and real-time collaboration tools.</p>
              <Link to="/register" className="feature-link">Learn more <i className="fas fa-arrow-right"></i></Link>
            </div>

            <div className="feature-card">
              <div className="feature-icon red">
                <i className="fas fa-bell"></i>
              </div>
              <h3>Alerts & Notifications</h3>
              <p>Stay informed with automated alerts for upcoming deadlines and compliance issues.</p>
              <Link to="/register" className="feature-link">Learn more <i className="fas fa-arrow-right"></i></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="landing-countries">
        <div className="countries-container">
          <div className="section-header">
            <h2>Regional Compliance Support</h2>
            <p>Pre-configured compliance rules for multiple countries and growing</p>
          </div>
          <div className="countries-grid">
            <div className="country-badge">
              <span className="country-flag">🇺🇬</span>
              <span>Uganda (NEMA)</span>
            </div>
            <div className="country-badge">
              <span className="country-flag">🇰🇪</span>
              <span>Kenya (NEMA)</span>
            </div>
            <div className="country-badge">
              <span className="country-flag">🇹🇿</span>
              <span>Tanzania (NEMC)</span>
            </div>
            <div className="country-badge">
              <span className="country-flag">🇷🇼</span>
              <span>Rwanda (REMA)</span>
            </div>
            <div className="country-badge">
              <span className="country-flag">🇳🇬</span>
              <span>Nigeria (NESREA)</span>
            </div>
            <div className="country-badge">
              <span className="country-flag">🇬🇭</span>
              <span>Ghana (EPA)</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="cta-container">
          <h2>Ready to streamline your compliance?</h2>
          <p>Manage your environmental, social, and health & safety requirements with SafeWork360.</p>
          <div className="cta-actions">
            <Link to="/register" className="btn-cta-primary">
              <i className="fas fa-rocket me-2"></i>
              Get Started Free
            </Link>
            <a href="#features" className="btn-cta-secondary">
              <i className="fas fa-play-circle me-2"></i>
              Watch Demo
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <i className="fas fa-shield-alt"></i>
              <span>SafeWork<span className="brand-360">360</span></span>
            </Link>
            <p>Environmental & OHS Compliance Platform for businesses and regulators worldwide.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#demo">Demo</a>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <a href="#about">About Us</a>
              <a href="#contact">Contact</a>
              <a href="#careers">Careers</a>
            </div>
            <div className="footer-column">
              <h4>Legal</h4>
              <a href="#privacy">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 SafeWork360. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
